import moment from 'moment'
import i18n from '../i18n'
import GlobalNotification from '../component/common/snackbar'
import { isCustomDateRangeValid } from '../utils/period-date'

/**
 * 订单类型文字转换
 * @param value
 * @returns
 */
export const formatEatTypeText = (value: number) => {
    const mapArr = new Map([
        [0, i18n.t('first_menu_tabs_component_text_eat_in')],
        [1, i18n.t('immediate_Dine_takeout')],
        [2, i18n.t('immediate_delivery')],
        [3, i18n.t('immediate_pick_store')],
        [5, i18n.t('immediate_eat_type_park')],
    ])
    return mapArr.get(value) || ''
}

/**
 * 菜品制作状态
 * @param value
 */
export const formatDishMakeStatusText = (value: number | null) => {
    if (value === null) {
        return i18n.t('immediate_not_made')
    }
    const mapArr = new Map([
        [0, i18n.t('immediate_not_made')],
        [1, i18n.t('immediate_made')],
        [2, i18n.t('immediate_served')],
        [3, i18n.t('immediate_pause_production')],
        [5, i18n.t('immediate_not_possible_make')],
        [11, i18n.t('immediate_prepared_dishes')],
    ])
    return mapArr.get(value) || i18n.t('immediate_unknown')
}

// 排序
export type SortType = {
    // 升序降序
    value?: 'ASC' | 'DESC';
    // 当前排序的key
    key?: string
}

/**
 * 前端排序
 * @param sort
 * @param value
 */
export const handleSort = (sort: SortType, value: any[], type: 'Date' | 'Number' | 'String') => {
    const key = sort.key || '';
    if (key) {
        if (type === 'Date') {
            if (sort.value === 'ASC') {
                // 升序
                return value.sort((a, b) => Date.parse(`${a[key] || ''}`) - Date.parse(`${b[key || '']}`))
            } else {
                // 降序
                return value.sort((a, b) => Date.parse(`${b[key] || ''}`) - Date.parse(`${a[key || '']}`))
            }
        } else if (type === 'Number') {
            if (sort.value === 'ASC') {
                // 升序
                return value.sort((a, b) => Number(`${a[key] || ''}`) - Number(`${b[key || '']}`))
            } else {
                // 降序
                return value.sort((a, b) => Number(`${b[key] || ''}`) - Number(`${a[key || '']}`))
            }
        } else if (type === 'String') {
            if (sort.value === 'ASC') {
                // 升序（字典序，支持中英文）
                return value.sort((a, b) => String(a[key] ?? '').localeCompare(String(b[key] ?? ''), undefined, { sensitivity: 'base' }))
            } else {
                // 降序
                return value.sort((a, b) => String(b[key] ?? '').localeCompare(String(a[key] ?? ''), undefined, { sensitivity: 'base' }))
            }
        }
    } else {
        return value
    }
}

/**
 * 格式化时间
 * @param date
 * @returns
 */
export const dateFormat = (date: string, format = 'YYYY/MM/DD HH:mm') => {
    return moment(date, moment.HTML5_FMT.DATETIME_LOCAL_MS).format(format)
}

// ========================= 风控相关通用格式化方法 =========================

// 金额格式化（统一为 € 保留两位小数）
export const formatRiskMoney = (val?: number | null) => {
    if (val === undefined || val === null) return '-';
    return `€ ${Number(val).toFixed(2)}`;
};

// 用餐时长（分钟）
export const formatRiskMinutes = (val?: number | null) => {
    if (val === undefined || val === null) return '-';
    return `${val}`;
};

// 风控类型文案映射
const RISK_TYPE_TEXT_MAP: Record<string, string> = {
    '1': 'intelligent_risk_control_page_risk_tag_max_discount_amount',
    '2': 'intelligent_risk_control_page_risk_tag_max_refund_count',
    '3': 'intelligent_risk_control_page_risk_tag_max_refund_amount',
    '4': 'intelligent_risk_control_page_risk_tag_avg_dish_count_abnormal',
    '5': 'intelligent_risk_control_page_risk_tag_max_dining_duration_abnormal',
};

export const formatRiskTypeText = (riskType?: number | null) => {
    if (!riskType) return '-';
    return RISK_TYPE_TEXT_MAP[riskType] ?? `${riskType}`;
};

// 风控状态文案映射：0待确认 1已忽略 2已解决
const RISK_STATUS_TEXT_MAP: Record<number, string> = {
    0: 'intelligent_risk_control_page_status_pending',
    1: 'intelligent_risk_control_page_status_ignored',
    2: 'intelligent_risk_control_page_status_resolved',
};

export const formatRiskStatusText = (status?: number | null) => {
    if (status === undefined || status === null) return RISK_STATUS_TEXT_MAP[0];
    return RISK_STATUS_TEXT_MAP[status] ?? `${status}`;
};

// 风控状态颜色映射
const RISK_STATUS_COLOR_MAP: Record<number, string> = {
    0: 'var(--func-error)',
    1: 'var(--func-info)',
    2: 'var(--func-success)',
};

export const getRiskStatusColor = (statusColorKey?: number | null) => {
    return RISK_STATUS_COLOR_MAP[statusColorKey ?? 0];
};

// 风险事件类型文案
const EVENT_TYPE_TEXT_MAP: Record<number, string> = {
    1: 'intelligent_risk_control_page_event_type_fast_close',
    2: 'intelligent_risk_control_page_event_type_cancel',
};

export const formatEventTypeText = (eventType?: number | null) => {
    if (eventType === undefined || eventType === null) return '-';
    return EVENT_TYPE_TEXT_MAP[eventType] ?? `${eventType}`;
};


// 校验当天日期逻辑
export const validateCurrentDate = () => {
    let startTime: string;
    let endTime: string;
    // 现在的时间
    const presentDate = new Date();
    /**
     * 根据现在的时刻  即商家进入系统初始化的时刻  判断获取数据是从哪一天开始获取
     * 1. 商家在04:00 - 23:59 时，看的是当天 04:00 - 次日 04:00 的数据
     * 2. 商家在00:00 - 03:59 时，看的是昨日 04:00 - 当日 04:00 的数据
     */
    if (moment(presentDate).isBefore(moment('04:00', 'HH:mm'))) {
        // 开始时间是00:00 - 03:59，取昨日 04:00 - 当日 04:00 的数据
        startTime = moment(presentDate).subtract(1, 'day').format('YYYY-MM-DD 04:00');
        endTime = moment(presentDate).format('YYYY-MM-DD 04:00');
    } else {
        // 开始时间是04:00 - 23:59，取当天 04:00 - 次日 04:00 的数据
        startTime = moment(presentDate).format('YYYY-MM-DD 04:00');
        endTime = moment(presentDate).add(1, 'day').format('YYYY-MM-DD 04:00');
    }
    return {
        startTime,
        endTime
    }
}

/** current-business-data 的 type 与 models 中 CurrentBusinessDataType 一致 */
export type CurrentBusinessDataTimeType = '0' | '1' | '2';

/**
 * GET /v1/restaurant/{id}/current-business-data 的 start/end 与 type 对应关系（与进入系统时刻联动）。
 * - 营业日 04:00–次日 04:00 由 {@link validateCurrentDate} 决定；凌晨 0–4 点进入时锚定「昨日」营业日，午餐/晚餐随之取昨日/今晨的区间。
 * - type=0 全天：当日营业日 04:00–次日 04:00
 * - type=1 午餐：06:00–17:59（该营业日自然日）
 * - type=2 晚餐：18:00–次日 05:59（该营业日当晚至次日凌晨）
 */
export const getCurrentBusinessDataTimeRange = (
    type: CurrentBusinessDataTimeType = '0'
): { start: string; end: string } => {
    const { startTime, endTime } = validateCurrentDate();
    // 营业日起算「日」= startTime 的日历日（含凌晨 0–3 点时的前一日 04:00 锚点）
    const anchor = moment(startTime, 'YYYY-MM-DD HH:mm');
    const dayStr = anchor.format('YYYY-MM-DD');
    const nextDayStr = anchor.clone().add(1, 'day').format('YYYY-MM-DD');
    const fullStart = moment(startTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss');
    const fullEnd = moment(endTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss');

    if (type === '0') {
        return { start: fullStart, end: fullEnd };
    }
    if (type === '1') {
        return {
            start: `${dayStr} 06:00:00`,
            end: `${dayStr} 17:59:59`,
        };
    }
    return {
        start: `${dayStr} 18:00:00`,
        end: `${nextDayStr} 05:59:59`,
    };
}

/**
 * 自定义日期范围校验：结束不早于开始，且跨度不超过 {@link CUSTOM_DATE_RANGE_MAX_MONTHS} 个月（与 App 一致）
 * @param startDate - 开始日期
 * @param endDate - 结束日期
 * @returns 是否通过校验
 */
export const dateValidate = (startDate: string | Date, endDate: string | Date) => {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const orderValid = end.isSame(start, 'day') || end.isAfter(start);
    const withinSpan = isCustomDateRangeValid(startDate, endDate);
    if (!orderValid) {
        GlobalNotification.error(i18n.t('business_analysis_page_text_end_time_cannot_earlier_than_start_time'));
    } else if (!withinSpan) {
        GlobalNotification.error(i18n.t('business_analysis_page_the_date_interval_cannot_exceed_31'));
    }
    return orderValid && withinSpan;
};